import type { ConfidenceLevel } from '../api/suggestions';
import type { RowConfidence } from '../api/draft';
import type { ConfidenceState } from '../components/ui/ConfidenceBar.vue';

export type TriageConfidence = ConfidenceLevel | 'confirmed' | 'rejected' | 'none';

// A draft cell is flagged uncertain when the agent's target OR hours confidence
// is low. Single source for both cues that explain it: the grid's amber corner
// flag and the cell card's "why". Confirming a row clears the flag — callers
// gate this on the row's `confirmed` state, they don't fold it in here.
export function isUncertain(c: RowConfidence): boolean {
  return c.target === 'low' || c.hours === 'low';
}

// Maps the API's ConfidenceLevel ('high'|'medium'|'low') onto the ConfidenceBar
// component's state vocabulary ('high'|'med'|'low'|...). The only mismatch is
// 'medium' → 'med'; kept in one place so the grid and cards stay consistent.
export function confidenceBarState(level: ConfidenceLevel): ConfidenceState {
  switch (level) {
    case 'high': return 'high';
    case 'medium': return 'med';
    case 'low': return 'low';
  }
}

export function confidenceVar(level: TriageConfidence): string {
  switch (level) {
    case 'high': return 'var(--conf-high)';
    case 'medium': return 'var(--conf-med)';
    case 'low': return 'var(--conf-low)';
    case 'rejected': return 'var(--conf-dismissed)';
    case 'confirmed': return 'var(--brand-primary)';
    default: return 'var(--conf-dismissed)';
  }
}

export function confidenceBgVar(level: TriageConfidence): string {
  switch (level) {
    case 'high': return 'var(--conf-high-bg)';
    case 'medium': return 'var(--conf-med-bg)';
    case 'low': return 'var(--conf-low-bg)';
    case 'rejected':
    default: return 'var(--conf-dismissed-bg)';
  }
}

export function confidenceCssClass(level: TriageConfidence): string {
  switch (level) {
    case 'high': return 'conf-high';
    case 'medium': return 'conf-med';
    case 'low': return 'conf-low';
    case 'confirmed': return 'conf-confirmed';
    case 'rejected': return 'conf-dismissed';
    default: return 'conf-dismissed';
  }
}
